import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ADMIN_TABLE_FETCH_SIZE, fighterRequestStatus, fighterRequestType } from '../constants';
import useDebounce from '../hooks/use-debounce';
import { getDocs, limit, orderBy, query, startAfter, where } from 'firebase/firestore';
import { fighterRequestsCollection } from '../utils/firebase';
import { ChevronDown, ChevronUp, Image } from 'lucide-react';
import { formatFirestoreTimestamp } from "../utils/common"
import { twMerge } from 'tailwind-merge';
import { useNavigate } from 'react-router-dom';
import { MANAGE_FIGHTER_REQUEST_PATH } from '../App';

export default function NewRegisterPendingList() {
  const [requests, setRequests] = useState(null)
  const [lastDoc, setLastDoc] = useState(null);
  const [sort, setSort] = useState("updateAt");
  const [order, setOrder] = useState("asc");
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate()

  const size = ADMIN_TABLE_FETCH_SIZE;
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);
  const observerRef = useRef(null);
  const loadingRef = useRef(false);

  const handleTableHeaderClick = (field) => {
    if (["fighterPicture", "totalWin", "totalLoss", "totalDraw"].includes(field)) return;
    if (field === sort) {
      setOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(field);
      setOrder('asc');
    }
  }

  const buildQuery = useCallback((reset = false) => {
    const constraints = [
      where("status", "==", fighterRequestStatus.pending),
      where("type", "==", fighterRequestType.newRegister)
    ];

    if (debouncedSearchTerm.trim() !== "") {
      constraints.push(where("name", ">=", debouncedSearchTerm));
      constraints.push(where("name", "<=", debouncedSearchTerm + '\uf8ff'));
      constraints.push(orderBy("name", "asc"));
    } else {
      constraints.push(orderBy(sort, order));
    }

    constraints.push(limit(size));
    if (!reset && !!lastDoc) constraints.push(startAfter(lastDoc));

    return query(fighterRequestsCollection, ...constraints);
  }, [debouncedSearchTerm, lastDoc, order, size, sort])

  const fetchData = useCallback(async (reset = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);

    try {
      const q = buildQuery(reset);
      const snapshot = await getDocs(q);
      const fetchedRequests = snapshot?.docs?.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) ?? [];

      setRequests((prev) => {
        if (reset) return fetchedRequests;
        const arr = [...prev, ...fetchedRequests];
        const seenIds = new Set();
        return arr.filter(item => {
          if (seenIds.has(item.id)) return false;
          seenIds.add(item.id);
          return true;
        });
      });

      const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;
      setLastDoc(lastVisible);
      setHasMore(snapshot.docs.length === size);
    } catch (error) {
      console.error('Error fetching fighters data: ', error);
    }
    loadingRef.current = false;
    setLoading(false);
  }, [buildQuery, size]);

  const handleRowClick = (request) => {
    navigate(`${MANAGE_FIGHTER_REQUEST_PATH}/${request.id}`)
  }

  useEffect(() => {
    setRequests([]);
    setLastDoc(null);
    setHasMore(true);
    fetchData(true);
  }, [sort, order, debouncedSearchTerm]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loadingRef.current) {
        fetchData();
      }
    });

    const currentObserverRef = observerRef.current;
    if (currentObserverRef) observer.observe(currentObserverRef);
    return () => {
      if (currentObserverRef) observer.unobserve(currentObserverRef);
    };
  }, [hasMore, fetchData]);

  const headers = {
    "name": "Name",
    "authIdentifier": "Identifier",
    "status": "Status",
    "type": "Type",
    "updateAt": "Update At"
  }

  if (!requests) {
    return <div className="w-full min-h-[calc(100vh-6.5rem)] flex-center">
      <div className="loading loading-md loading-bars" />
    </div>
  }

  return (
    <div className='flex flex-row w-full h-full'>
      <div className='flex-center flex-1 items-start w-full overflow-auto h-full'>
        <div className="overflow-auto w-full h-full">
          <table className="table min-w-full w-auto">
            <thead>
              <tr className='[&>th]:text-center'>
                <th></th>
                {Object.keys(headers).map((key) => (
                  <th key={key} className='hover:bg-base-200 cursor-pointer' onClick={() => handleTableHeaderClick(key)}>
                    <div className='flex flex-row justify-center items-center gap-2'>
                      {key === "fighterPicture" ? <Image /> : <p>{headers[key]}</p>}
                      {sort === key && (order === "desc" ? <ChevronUp /> : <ChevronDown />)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {requests.map((request, index) => (
                <tr key={index} className='hover [&>td]:text-nowrap cursor-pointer' onClick={() => handleRowClick(request)}>
                  <td className='text-center font-bold'>{index + 1}</td>
                  <td>{request.name}</td>
                  <td>{request.authIdentifier}</td>
                  <td>
                    <p className={twMerge('font-bold text-center', request.status === "PENDING" ? "text-warning" : "")}>
                      {request.status?.toUpperCase() ?? "?"}
                    </p>
                  </td>
                  <td className='font-bold text-center'>{request.type}</td>
                  <td>{formatFirestoreTimestamp(request.updateAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className='flex-center w-full p-2'>
            {loading && <div className='loading loading-bars' />}
            {!loading && requests.length === 0 && <p className='flex-center h-36'>NO RESULT</p>}
            {!loading && requests.length > 0 && !hasMore && <p className='opacity-70'>END</p>}
          </div>
          <div ref={observerRef} style={{ height: '1px' }} />
        </div>
        <div className="divider divider-primary m-0" />
        <div className='w-full'>
          <div className='flex-center flex-row p-4 pt-2 gap-2'>
            <input type="text" className='input input-bordered w-full' placeholder='Search by Name...' 
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
        </div>
      </div>
    </div>
  )
}